import {
    GameIntroColors,
    GameIntroViewConfig,
    StampConfig,
    Volatility
} from "@nolimitcity/slot-launcher/bin/plugins/concreteplugins/nolimitgameintro/GameIntroView";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {SlideShowPage} from "@nolimitcity/slot-launcher/bin/gui/views/slideshow/SlideShowPage";
import {BookOfShadowsGameAssets} from "../BookOfShadowsGameAssets";
import {BookOfShadowsSlideShowPage} from "./BookOfShadowsSlideShowPage";
import {BookOfShadows} from "../BookOfShadows";

interface IPagesConfig {
    name: string,
    loopStart: number,
    loopTill: number
}

export class BookOfShadowsIntroConfig implements GameIntroViewConfig {
    public static INTRO_BG = BookOfShadowsGameAssets.INTRO_BACKGROUND;
    public static INTRO_LOGO = BookOfShadowsGameAssets.INTRO_LOGO;
    public static INTRO_FEATURE_1 = BookOfShadowsGameAssets.INTRO_FEATURE_1;
    public static INTRO_FEATURE_1_SUB = BookOfShadowsGameAssets.INTRO_FEATURE_1_SUB;
    public static INTRO_FEATURE_2 = BookOfShadowsGameAssets.INTRO_FEATURE_2;
    public static INTRO_FEATURE_2_SUB = BookOfShadowsGameAssets.INTRO_FEATURE_2_SUB;
    public static INTRO_FEATURE_3 = BookOfShadowsGameAssets.INTRO_FEATURE_3;
    public static INTRO_FEATURE_4 = BookOfShadowsGameAssets.INTRO_FEATURE_4;
    public static INTRO_FEATURE_5 = BookOfShadowsGameAssets.INTRO_FEATURE_5;
    public static INTRO_DIVIDER = BookOfShadowsGameAssets.INTRO_DIVIDER;

    colors: GameIntroColors = {
        slideShowColors: {
            backgroundColor: 0x000000,
            backgroundAlpha: 0.7,
            bulletColor: 0xffffff
        },
        stampColor: 0xFFFFFF,
    };
    stampConfig: StampConfig = {
        xWays: false,
        xNudge: false,
        volatility: Volatility.EXTREME
    };
    background: PIXI.Container;
    borderBottom: PIXI.Container;
    borderTop: PIXI.Container;
    logo: PIXI.Container;
    pages: SlideShowPage[];

    private _pagesConfig: IPagesConfig[] = [
        {
            name: BookOfShadowsIntroConfig.INTRO_FEATURE_1,
            loopStart: 1,
            loopTill: 22
        },
        {
            name: BookOfShadowsIntroConfig.INTRO_FEATURE_2,
            loopStart: 4,
            loopTill: 16
        },
        {
            name: BookOfShadowsIntroConfig.INTRO_FEATURE_3,
            loopStart: 0,
            loopTill: 8
        }
    ];
    private freespins: BookOfShadowsSlideShowPage;
    private shadowRows: BookOfShadowsSlideShowPage;
    private luckyLocks: BookOfShadowsSlideShowPage;
    private gamble_5050: SlideShowPage;
    private gamble_bonusWheel: SlideShowPage;

    /**
     * @inheritDoc
     */
    public init(): Promise<GameIntroViewConfig> {
        const imgLoader: ImgLoader = new ImgLoader(NolimitApplication.resourcePath);

        let name: string,
            pageConfig: IPagesConfig;

        imgLoader.add(BookOfShadowsIntroConfig.INTRO_BG, BookOfShadowsIntroConfig.INTRO_BG);
        imgLoader.add(BookOfShadowsIntroConfig.INTRO_LOGO, BookOfShadowsIntroConfig.INTRO_LOGO);
        imgLoader.add(BookOfShadowsIntroConfig.INTRO_FEATURE_1_SUB, BookOfShadowsIntroConfig.INTRO_FEATURE_1_SUB);
        imgLoader.add(BookOfShadowsIntroConfig.INTRO_FEATURE_2_SUB, BookOfShadowsIntroConfig.INTRO_FEATURE_2_SUB);
        imgLoader.add(BookOfShadowsIntroConfig.INTRO_FEATURE_4, BookOfShadowsIntroConfig.INTRO_FEATURE_4);
        imgLoader.add(BookOfShadowsIntroConfig.INTRO_FEATURE_5, BookOfShadowsIntroConfig.INTRO_FEATURE_5);
        imgLoader.add(BookOfShadowsIntroConfig.INTRO_DIVIDER, BookOfShadowsIntroConfig.INTRO_DIVIDER);

        for (let i: number = 0; i < this._pagesConfig.length; i++) {
            pageConfig = this._pagesConfig[i];

            for (let j: number = pageConfig.loopStart; j <= pageConfig.loopTill; j++) {
                name = pageConfig.name;
                if (j < 10) {
                    name += "0";
                }
                name += j + ".png";

                imgLoader.add(name, name);
            }
        }

        return imgLoader.load().then(() => {
            this.initGraphics();
            return this;
        });
    }

    public gameClientConfigurationApplied(): void {
        if (BookOfShadows.api.gameClientConfiguration.boostedBetExtraRows) {
            this.pages.push(this.shadowRows);
        }

        if (BookOfShadows.api.gameClientConfiguration.boostedBetLockedReels) {
            this.pages.push(this.luckyLocks);
        }

        if (BookOfShadows.api.gameClientConfiguration.gambleFiftyFiftyAllowed) {
            this.pages.push(this.gamble_5050);
        }

        if (BookOfShadows.api.gameClientConfiguration.gambleIntoBonusAllowed) {
            this.pages.push(this.gamble_bonusWheel);
        }
    }

    /**
     * @description To initialize the graphics.
     */
    private initGraphics() {
        this.background = new PIXI.Container();
        const bgSprite: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_BG));
        bgSprite.anchor.set(0.5);
        this.background.addChild(bgSprite);

        this.borderTop = new PIXI.Container();
        const topSprite: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_DIVIDER));
        topSprite.anchor.set(0.5);
        this.borderTop.addChild(topSprite);

        this.borderBottom = new PIXI.Container();
        const bottomSprite: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_DIVIDER));
        bottomSprite.anchor.set(0.5);
        bottomSprite.scale.y *= -1;
        this.borderBottom.addChild(bottomSprite);

        this.logo = new PIXI.Container();
        const logoSprite: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_LOGO));
        logoSprite.anchor.set(0.5);
        this.logo.addChild(logoSprite);

        this.createPages();
    }

    /**
     * @description To create the pages.
     */
    private createPages(): void {
        const featureText1_1: string = BookOfShadows.api.translations.translate("Golden symbols expand!"),
            featureText1_2: string = BookOfShadows.api.translations.translate("Three or more Book of Shadows symbols trigger Free Spins."),
            featureText2_1: string = BookOfShadows.api.translations.translate("Choose to play with 3, 4 or 5 rows."),
            featureText2_2: string = BookOfShadows.api.translations.translate("Add Shadow Rows feature by clicking upper rows."),
            featureText3_1: string = BookOfShadows.api.translations.translate("Lock reels of your choice and spin."),
            featureText3_2: string = BookOfShadows.api.translations.translate("A locked reel will adjust the bet."),
            featureText4: string = BookOfShadows.api.translations.translate("Gamble your winnings in 50/50. Double or Nothing - win and double the winnings!"),
            featureText5: string = BookOfShadows.api.translations.translate("Gamble your winnings in Wheel of Bonus to win Free spins feature."),
            page1To3Textures: PIXI.Texture[][] = [],
            fps: number = 6;

        let name: string,
            pageConfig: IPagesConfig;

        for (let i: number = 0; i < this._pagesConfig.length; i++) {
            pageConfig = this._pagesConfig[i];
            page1To3Textures.push([]);

            for (let j: number = pageConfig.loopStart; j <= pageConfig.loopTill; j++) {
                name = pageConfig.name;
                if (j < 10) {
                    name += "0";
                }
                name += j + ".png";

                page1To3Textures[i].push(ImgLoader.getImgTexture(name));
            }
        }

        this.freespins = new BookOfShadowsSlideShowPage(page1To3Textures[0], fps, featureText1_1, featureText1_2, ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_FEATURE_1_SUB));
        this.shadowRows = new BookOfShadowsSlideShowPage(page1To3Textures[1], fps, featureText2_1, featureText2_2, ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_FEATURE_2_SUB));
        this.luckyLocks = new BookOfShadowsSlideShowPage(page1To3Textures[2], (fps + 1), featureText3_1, featureText3_2, PIXI.Texture.EMPTY);
        this.gamble_5050 = new SlideShowPage(ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_FEATURE_4), featureText4);
        this.gamble_bonusWheel = new SlideShowPage(ImgLoader.getImgTexture(BookOfShadowsIntroConfig.INTRO_FEATURE_5), featureText5);

        this.pages = [this.freespins];
    }
}